import React from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  useTheme,
  useMediaQuery,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
} from '@mui/material';
import {
  Home as HomeIcon,
  List as ActivitiesIcon,
  CalendarToday as CalendarIcon,
  MenuBook as HowItWorksIcon,
  Login as LoginIcon,
  Menu as MenuIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import LanguageSwitcher from './LanguageSwitcher';
import { useLanguage } from '../hooks/useLanguage';

interface PublicNavigationBarProps {
  currentView: string;
  onViewChange: (view: string) => void;
  onLogin: () => void;
}

const PublicNavigationBar: React.FC<PublicNavigationBarProps> = ({
  currentView,
  onViewChange,
  onLogin,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
  const { t } = useLanguage();

  // A/B Test Flag: Read from global feature flags set by PublicHomePage
  const [showCalendarAndActivitiesMenu, setShowCalendarAndActivitiesMenu] = React.useState(false);

  React.useEffect(() => {
    const readFlag = () => {
      const globalWindow = window as typeof window & {
        __PUBLIC_FEATURE_FLAGS?: Record<string, unknown>;
      };
      const flagValue = !!globalWindow.__PUBLIC_FEATURE_FLAGS?.showCalendarAndActivitiesMenu;
      setShowCalendarAndActivitiesMenu(flagValue);
    };

    readFlag();
    const evt = 'treffpunkts:publicFeatureFlags';
    window.addEventListener(evt, readFlag);
    return () => window.removeEventListener(evt, readFlag);
  }, []);

  const SHOW_CALENDAR_AND_ACTIVITIES_MENU = showCalendarAndActivitiesMenu;

  const navigationItems = [
    { key: 'public-home', label: t('navigation.home'), icon: <HomeIcon /> },
    // A/B TEST: Activities and Calendar menu items - Hidden for A/B testing
    ...(SHOW_CALENDAR_AND_ACTIVITIES_MENU ? [
      { key: 'public-activities', label: t('navigation.activities'), icon: <ActivitiesIcon /> },
      { key: 'calendar', label: t('navigation.calendar'), icon: <CalendarIcon /> },
    ] : []),
    { key: 'how-it-works', label: t('navigation.howItWorks'), icon: <HowItWorksIcon /> },
    // Pricing moved to footer to reduce cost perception friction on first view
  ];

  const handleViewChange = (view: string) => {
    console.log('🔄 Navigation button clicked:', view);
    // Clear URL parameters to prevent deep link interference
    window.history.pushState({}, '', window.location.pathname);
    onViewChange(view);
    setMobileMenuOpen(false);
  };

  const handleLogin = () => {
    onLogin();
    setMobileMenuOpen(false);
  };

  const renderDesktopNavigation = () => (
    <>
      {/* Left side navigation items */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 2 }}>
        {navigationItems.map((item) => (
          <Button
            key={item.key}
            color="inherit"
            startIcon={item.icon}
            onClick={() => handleViewChange(item.key)}
            sx={{
              fontWeight: currentView === item.key ? 'bold' : 'normal',
              backgroundColor: currentView === item.key ? 'rgba(255,255,255,0.15)' : 'transparent',
              color: 'white',
              px: 2.5,
              py: 1,
              borderRadius: 2,
              textTransform: 'none',
              fontSize: '1rem',
              '&:hover': {
                backgroundColor: 'rgba(255,255,255,0.1)',
                transform: 'translateY(-1px)',
              },
              transition: 'all 0.2s ease-in-out',
            }}
          >
            {item.label}
          </Button>
        ))}
      </Box>

      {/* Right side items */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, ml: 'auto', pr: { xs: 1, sm: 1, md: 2 } }}>
        {/* White Language Switcher */}
        <LanguageSwitcher variant="icon-only" showLabel={false} whiteTheme={true} />
        
        {/* Login Button */}
        <Button
          color="inherit"
          variant="outlined"
          startIcon={<LoginIcon />}
          onClick={handleLogin}
          sx={{
            borderColor: 'rgba(255,255,255,0.6)',
            color: 'white',
            fontWeight: 600,
            textTransform: 'none',
            px: 3,
            py: 1,
            borderRadius: 2,
            '&:hover': {
              borderColor: 'white',
              backgroundColor: 'rgba(255,255,255,0.1)',
              transform: 'translateY(-1px)',
            },
            transition: 'all 0.2s ease-in-out',
          }}
        >
          {t('navigation.login')}
        </Button>
      </Box>
    </>
  );

  const renderMobileNavigation = () => (
    <>
      <IconButton
        color="inherit"
        edge="end"
        onClick={() => setMobileMenuOpen(true)}
      >
        <MenuIcon />
      </IconButton>
      
      <Drawer
        anchor="right"
        open={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
        PaperProps={{
          sx: { width: 280 }
        }}
      >
        <Box sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              TreffPunkts
            </Typography>
            <IconButton onClick={() => setMobileMenuOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
          
          <List>
            {navigationItems.map((item) => (
              <ListItem
                key={item.key}
                onClick={() => handleViewChange(item.key)}
                sx={{
                  cursor: 'pointer',
                  borderRadius: 1,
                  backgroundColor: currentView === item.key ? theme.palette.primary.main + '20' : 'transparent',
                  '&:hover': {
                    backgroundColor: theme.palette.primary.main + '10',
                  },
                }}
              >
                <ListItemIcon sx={{ color: currentView === item.key ? theme.palette.primary.main : 'inherit' }}>
                  {item.icon}
                </ListItemIcon>
              <ListItemText 
                  primary={item.label} 
                  sx={{ 
                    '& .MuiListItemText-primary': {
                      fontWeight: currentView === item.key ? 'bold' : 'normal',
                      color: currentView === item.key ? theme.palette.primary.main : 'inherit',
                    }
                  }}
                />
              </ListItem>
            ))}
            <ListItem sx={{ mt: 1 }}>
              <Box sx={{ width: '100%' }}>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                  Language
                </Typography>
                <LanguageSwitcher variant="compact" showLabel={false} />
              </Box>
            </ListItem>
            
            <ListItem
              onClick={handleLogin}
              sx={{
                cursor: 'pointer',
                borderRadius: 1,
                mt: 2,
                border: `1px solid ${theme.palette.primary.main}`,
                '&:hover': {
                  backgroundColor: theme.palette.primary.main + '10',
                },
              }}
            >
              <ListItemIcon sx={{ color: theme.palette.primary.main }}>
                <LoginIcon />
              </ListItemIcon>
              <ListItemText 
                primary={t('navigation.login')} 
                sx={{ 
                  '& .MuiListItemText-primary': {
                    fontWeight: 'bold',
                    color: theme.palette.primary.main,
                  }
                }}
              />
            </ListItem>
          </List>
        </Box>
      </Drawer>
    </>
  );

  return (
    <AppBar 
      position="fixed" 
      sx={{ 
        zIndex: theme.zIndex.drawer + 1,
        backgroundColor: theme.palette.primary.main,
      }}
    >
      <Toolbar sx={{ 
        px: { xs: 0, sm: 1, md: 2 },
        minHeight: { xs: 56, sm: 64 },
        justifyContent: 'space-between'
      }}>
        {/* Logo */}
        <Box
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            cursor: 'pointer',
            pl: { xs: 1, sm: 1, md: 2 } // Add left padding only to logo container
          }}
          onClick={() => handleViewChange('public-home')}
        >
          {/* Show logo on larger screens, text on smaller screens */}
          <Box
            sx={{
              display: { xs: 'none', sm: 'block' },
              '& img': {
                height: { sm: 50, md: 60, lg: 70 },
                width: 'auto',
                maxHeight: '70px',
                display: 'block',
              }
            }}
          >
            <img
              src="/treffpunkslogo-white.svg"
              alt="TreffPunkts"
            />
          </Box>
          
          {/* Text logo for mobile and very small screens */}
          <Typography
            variant="h6"
            sx={{
              display: { xs: 'block', sm: 'none' },
              fontWeight: 'bold',
              color: 'white',
              fontSize: { xs: '1.1rem', sm: '1.25rem' },
              textTransform: 'none',
              letterSpacing: '0.5px',
            }}
          >
            TreffPunkts
          </Typography>
        </Box>
        
        {isMobile ? renderMobileNavigation() : renderDesktopNavigation()}
      </Toolbar>
    </AppBar>
  );
};

export default PublicNavigationBar;
